import { useEffect, useRef, useState, type RefObject } from 'react';
import { ChevronRight, FileText, Lock } from 'lucide-react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import {
  fetchFreeResult,
  recordOfferImpression,
  type AssessmentType,
  type FreeResult,
} from '../../lib/apiClient';
import { useOfferImpressionOnView } from '../../lib/useOfferImpressionOnView';
import {
  buildResultView,
  type BottleneckResultView,
  type PerspectiveResultView,
  type WorkFitResultView,
} from '../../lib/assessment/resultView';
import { CheckoutButton } from '../report/CheckoutButton';
import { HpScorePanel } from '../report/HpScorePanel';
import { Button } from '../ui/button';
import { UNLOCK_CONTENT } from '../../lib/assessment/unlockContent';
import {
  WF_DIMENSION_EXPLANATIONS,
  WF_HOW_TO_USE,
  WF_INTERVIEW_EYEBROW,
  WF_PROFILE_CONTENT,
  WF_RECOGNITION,
} from '../../lib/assessment/workFitResultContent';
import { routes } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';

type PageState =
  | { phase: 'loading' }
  | { phase: 'not-found' }
  | { phase: 'error' }
  | { phase: 'ok'; result: FreeResult; loadedForId: string; loadedForToken: string };

/**
 * Production's page meta, one entry per assessment type.
 *
 * Production serves these from three separate routes with three separate
 * titles; V2 unifies them under `/results/:id`, so the title follows the
 * result's type once it loads. All three are `noindex` there, and a result
 * belongs to one customer, so all three are here too.
 */
const RESULT_META: Record<AssessmentType, { title: string; description: string }> = {
  bottleneck: {
    title: 'Your High Performer Type | High Performer OS',
    description:
      'Review your High Performer Type, top bottlenecks, score summary, and one immediate action based on your assessment result.',
  },
  work_fit: {
    title: 'Your Work Fit Profile | High Performer OS',
    description:
      'Your Work Fit Assessment results: profile, top environment needs, culture risks, and interview questions.',
  },
  perspective: {
    title: 'Your Perspective Pattern | High Performer OS',
    description:
      'Your Perspective Assessment results: dominant pattern, intensity, strength, cost, and immediate reframe.',
  },
};

/** Shown before the result loads and its type is known. */
const RESULT_META_PENDING = { title: 'Your Results | High Performer OS' };

/** Single result route: fetches by id and dispatches to the correct per-type view. */
export function ResultPage({ id }: { id: string }) {
  const { status, session } = useAuth();
  // Derived from `status` so the fetch gate matches the render gate below; a
  // password-recovery session carries a valid token but is not signed in.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  // Owned by ResultPage (not ReportCta) so the observer + fired-id history survive
  // ReportCta unmount/remount across loading/refetch/token-refresh transitions.
  const lockedOfferRef = useRef<HTMLDivElement | null>(null);

  const meta = state.phase === 'ok' ? RESULT_META[state.result.assessmentType] : null;
  usePageMeta({ ...(meta ?? RESULT_META_PENDING), noindex: true });

  useEffect(() => {
    if (status === 'loading') return;
    if (!token) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const result = await fetchFreeResult(token, id);
      if (cancelled) return;
      if (result.state === 'ok')
        setState({ phase: 'ok', result: result.result, loadedForId: id, loadedForToken: token });
      else if (result.state === 'not-found') setState({ phase: 'not-found' });
      else setState({ phase: 'error' });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, token, status]);

  // Record one offer_impression when the locked offer for the CURRENT request's
  // loaded, owned result first enters the viewport. Eligibility is bound to the id +
  // token that produced the successful state, so an id/token change flips
  // impressionEnabled false on the same render (before the passive fetch effect sets
  // loading), tearing down a stale observer before it can fire for the wrong request.
  // The producer uses the observer-bound id, never a current external variable.
  const okForCurrentRequest =
    state.phase === 'ok' &&
    state.loadedForId === id &&
    state.loadedForToken === token &&
    state.result.id === id
      ? state
      : null;
  const lockedResultId =
    okForCurrentRequest && !okForCurrentRequest.result.isUnlocked ? okForCurrentRequest.result.id : '';
  const impressionEnabled =
    status === 'signed-in' &&
    token !== null &&
    okForCurrentRequest !== null &&
    !okForCurrentRequest.result.isUnlocked;
  useOfferImpressionOnView(lockedOfferRef, impressionEnabled, lockedResultId, (observedResultId) => {
    if (!token) return;
    // Fire-and-forget: a failure must never surface to the user or block the UI.
    void recordOfferImpression(token, observedResultId).catch(() => {});
  });

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">Sign in to view your result.</p>
        <AuthStatus />
      </section>
    );
  }
  if (state.phase === 'loading')
    return <p className="text-muted-foreground">Loading your result…</p>;
  if (state.phase === 'not-found') return <p className="text-foreground">Result not found.</p>;
  if (state.phase === 'error')
    return <p className="text-destructive">Your result could not be loaded.</p>;

  const view = buildResultView(state.result);
  const body =
    view.kind === 'bottleneck' ? (
      <BottleneckResult view={view} result={state.result} />
    ) : view.kind === 'work_fit' ? (
      <WorkFitResult view={view} />
    ) : (
      <PerspectiveResult view={view} />
    );

  return (
    <div className="flex flex-col gap-8">
      {/*
        Production opens every result page with this, and it is the only way
        back from a result on a page it serves without a site header. V2's
        application shell offers a Dashboard link of its own, but the shell is
        still under review and production's affordance is the one customers
        were taught, so it is reproduced here rather than assumed redundant.
      */}
      <Link
        href={routes.dashboard}
        data-testid="result-back-to-dashboard"
        className="text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        ← Back to Dashboard
      </Link>
      {body}
      <ReportCta result={state.result} lockedRef={lockedOfferRef} />
    </div>
  );
}

/**
 * The unlock panel beneath every free result, copied from production's three
 * results pages.
 *
 * **The copy is per assessment type**, because production's is: each page names
 * eight or nine specific things the full report contains, and they are
 * different things. V2 previously showed one generic sentence here for all
 * three, which is the copy a customer reads at the moment they decide whether
 * to pay.
 *
 * **The action is V2's**, by Product Owner decision (2026-08-29): production is
 * early-access gated and renders a promo-code panel below these bullets, where
 * V2 renders its checkout. Everything above the action is production's.
 */
function ReportCta({
  result,
  lockedRef,
}: {
  result: FreeResult;
  lockedRef: RefObject<HTMLDivElement | null>;
}) {
  const content = UNLOCK_CONTENT[result.assessmentType];

  if (result.isUnlocked) {
    return (
      <div
        className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-6"
        data-testid="report-cta-unlocked"
      >
        <div className="flex flex-wrap items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <h2 className="text-base font-semibold">{content.unlockedHeading}</h2>
          <span
            className="rounded-md border border-green-600/30 bg-green-600/20 px-2 py-0.5 text-xs text-green-500"
            data-testid="report-cta-unlocked-badge"
          >
            Unlocked
          </span>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">{content.unlockedLead}</p>

        <Link
          href={routes.report(result.id)}
          data-testid="report-cta-open"
          className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Button className="w-full gap-2 sm:w-auto" tabIndex={-1}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            {content.unlockedAction}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Production prints this above the panel, on the Bottleneck page only. */}
      {content.bridge ? (
        <p
          className="px-2 text-center text-sm leading-relaxed text-muted-foreground"
          data-testid="report-cta-bridge"
        >
          {content.bridge}
        </p>
      ) : null}

      <div
        ref={lockedRef}
        className="flex flex-col gap-4 rounded-xl border border-primary/30 bg-card p-6"
        data-testid="report-cta-locked"
      >
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <h2 className="text-xl font-bold">{content.lockedHeading}</h2>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">{content.lead}</p>

        <ul className="flex flex-col gap-2" data-testid="report-cta-includes">
          {content.bullets.map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm">
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {content.footnote ? (
          <p className="text-xs italic text-muted-foreground" data-testid="report-cta-footnote">
            {content.footnote}
          </p>
        ) : null}

        <div>
          <CheckoutButton resultId={result.id} />
        </div>
      </div>
    </div>
  );
}

function ScoreTile({ label, value }: { label: string; value: number }) {
  return (
    /*
      Production puts the number first, in the primary colour, with the label
      beneath it as a small uppercase caption. V2 had the label on top and the
      number in body colour, which read as a form field rather than a score.
    */
    <div className="rounded-xl border border-border bg-card p-5 text-center">
      <div className="mb-1 text-3xl font-bold text-primary tabular-nums">{value}</div>
      <div className="text-xs uppercase leading-tight tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

/**
 * The Bottleneck free result.
 *
 * Rebuilt against the running production page, 2026-09-07. V2 had the right
 * copy in the right order but a much plainer presentation: the type name two
 * sizes down and left-aligned, the score as `35/100 · D (label)` on one line,
 * and the two section labels as ordinary headings. Production centres the type
 * block, sets the name at `text-4xl md:text-5xl`, renders the full score panel
 * with its grade bar and badge, and writes both section labels as small
 * uppercase eyebrows — muted for the bottlenecks, primary for the one move.
 *
 * This is the page that asks for the sale, so the difference was not cosmetic.
 */
function BottleneckResult({ view, result }: { view: BottleneckResultView; result: FreeResult }) {
  return (
    <section className="flex flex-col gap-10" data-testid="result-bottleneck">
      <div className="flex flex-col gap-4 text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">
          Your High Performer Type
        </p>
        <h1 className="text-4xl font-bold md:text-5xl">{view.primaryType}</h1>
        <p className="mx-auto max-w-xl text-lg leading-relaxed text-muted-foreground">
          {view.shortExplanation}
        </p>
      </div>

      <HpScorePanel result={result} testIdPrefix="result" />

      <div className="grid grid-cols-2 gap-3">
        <ScoreTile label="Bottleneck Score" value={view.scores.bottleneck} />
        <ScoreTile label="Execution Readiness" value={view.scores.executionReadiness} />
        <ScoreTile label="Recovery Gap" value={view.scores.recoveryGap} />
        <ScoreTile label="Clarity Score" value={view.scores.clarity} />
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Your Top Bottlenecks
        </h3>
        {view.topBottlenecks.map((b) => (
          <div key={b.key} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span className="text-base font-semibold">{b.label}</span>
            </div>
            <p className="text-foreground">{b.explanation}</p>
            <p className="text-sm">
              <span className="font-medium">Try this today: </span>
              {b.action}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-primary">
          One Move to Make Today
        </h3>
        <p className="mt-3 text-foreground">{view.oneMove}</p>
      </div>
    </section>
  );
}

/**
 * The Work Fit free result, copied from production's `work-fit-results.tsx`.
 *
 * V2 previously showed the profile name, one sentence, and a bare list of
 * dimension labels. Production explains each of those dimensions, adds a
 * recognition paragraph for the profile, tells the customer how to use the
 * result, names two culture risks to watch for, and gives them a question to
 * take into a hiring conversation. All of it is copy the customer reads before
 * deciding whether the full report is worth buying.
 */
function WorkFitResult({ view }: { view: WorkFitResultView }) {
  const recognition = WF_RECOGNITION[view.typeSlug ?? ''];
  const profile = WF_PROFILE_CONTENT[view.typeSlug ?? ''];

  return (
    <section className="flex flex-col gap-6" data-testid="result-work_fit">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Work Fit Profile</p>
        <h1 className="text-3xl font-semibold">{view.primaryType}</h1>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">What this means for you</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{view.shortExplanation}</p>
        {/* Production separates the recognition paragraph with a rule. */}
        {recognition ? (
          <p
            className="border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground"
            data-testid="work-fit-recognition"
          >
            {recognition}
          </p>
        ) : null}
      </div>

      {view.topDimensions.length > 0 ? (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">Your Strongest Environment Needs</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {view.topDimensions.map((d) => (
              <div
                key={d.key}
                className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5"
                data-testid="work-fit-dimension"
                data-dimension={d.key}
              >
                <span className="w-fit rounded-md border border-primary/30 px-2 py-0.5 text-xs text-primary">
                  Top Environment Need
                </span>
                <p className="text-sm font-semibold">{d.label}</p>
                {WF_DIMENSION_EXPLANATIONS[d.key] ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {WF_DIMENSION_EXPLANATIONS[d.key]}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">How to use this result</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{WF_HOW_TO_USE}</p>
      </div>

      {profile ? (
        <div className="flex flex-col gap-4" data-testid="work-fit-risks">
          <h2 className="text-lg font-semibold">Work Environment Risks to Watch For</h2>
          <div className="flex flex-col gap-3">
            {profile.cultureRisks.map((risk) => (
              <div
                key={risk}
                className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5"
                data-testid="work-fit-risk"
              >
                <span className="text-xs font-bold uppercase tracking-widest text-destructive/70">
                  Risk Signal
                </span>
                <p className="text-sm leading-relaxed text-muted-foreground">{risk}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {profile ? (
        <div
          className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6"
          data-testid="work-fit-interview"
        >
          <h2 className="text-lg font-semibold">Interview Question to Ask</h2>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {WF_INTERVIEW_EYEBROW}
          </p>
          <blockquote className="border-l-4 border-primary py-1 pl-4">
            <p className="text-sm italic leading-relaxed" data-testid="work-fit-question">
              &ldquo;{profile.interviewQuestion}&rdquo;
            </p>
          </blockquote>
        </div>
      ) : null}
    </section>
  );
}

function PerspectiveResult({ view }: { view: PerspectiveResultView }) {
  return (
    <section className="flex flex-col gap-6" data-testid="result-perspective">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Perspective Pattern</p>
        <h1 className="text-3xl font-semibold">{view.primaryType}</h1>
        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          <span className="rounded-md bg-secondary px-2 py-1">Intensity: {view.intensityLabel}</span>
          {view.isBlended && view.secondaryPatternLabel ? (
            <span className="rounded-md bg-secondary px-2 py-1">Blended with {view.secondaryPatternLabel}</span>
          ) : null}
        </div>
        {view.patternContent ? (
          <p className="mt-2 italic text-muted-foreground">{view.patternContent.coreLens}</p>
        ) : null}
        <p className="mt-2 text-foreground">{view.shortExplanation}</p>
      </div>

      {view.patternContent ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border p-4">
            <div className="font-medium">When This Pattern Works For You</div>
            <p className="mt-1 text-foreground">{view.patternContent.strength}</p>
          </div>
          <div className="rounded-lg border border-border p-4">
            <div className="font-medium">When This Pattern Works Against You</div>
            <p className="mt-1 text-foreground">{view.patternContent.cost}</p>
          </div>
          <div className="rounded-lg border border-border p-4">
            <div className="font-medium">Immediate Reframe</div>
            <p className="mt-1 text-foreground">{view.patternContent.immediateReframe}</p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium">Your Top Pattern Scores</h2>
        {view.topPatterns.map((p) => (
          <div
            key={p.key}
            className="flex items-center justify-between rounded-lg border border-border p-3"
          >
            <span>{p.label}</span>
            <span className="font-mono">{p.score.toFixed(2)}</span>
          </div>
        ))}
        {/*
          Production does not hide the rest of the map — it renders it blurred
          behind a lock, so the customer can see how much they are not being
          shown. The blurred layer is `aria-hidden`, because a screen reader
          cannot perceive a blur and would otherwise read out the very scores
          the panel exists to withhold; the lock message beside it carries the
          meaning instead.
        */}
        {view.lockedPatterns.length > 0 ? (
          <div
            className="relative overflow-hidden rounded-lg border border-border"
            data-testid="perspective-locked-map"
          >
            <div className="select-none space-y-4 p-6 blur-sm" aria-hidden="true">
              {view.lockedPatterns.map((p) => (
                <div key={p.key} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{p.label}</span>
                    <span className="tabular-nums text-muted-foreground">{p.score.toFixed(2)}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full bg-muted-foreground/60"
                      style={{ width: `${Math.round((p.score / 5) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/80 px-6 text-center backdrop-blur-[2px]">
              <Lock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm font-semibold">Complete Pattern Map Locked</p>
              <p className="text-xs text-muted-foreground">
                The full report unlocks your complete 8-pattern map, secondary patterns, and how
                each one is affecting you.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
